import * as React from "react";
import { cn } from "@/lib/utils";
import { CounterButton } from "./Button";
import { ScreenTitle } from "./Panel";

/**
 * The full-screen stop (DESIGN.md §7): the till cannot continue, and the
 * cashier needs one sentence and one next step, not a banner they can dismiss.
 *
 * Three kinds only:
 *   offline   the connection is gone — say what still works and what does not
 *   blocked   the register, shift or location is not in a state that allows this
 *   missing   nothing to show yet, and nothing the cashier did caused it
 *
 * There is no "upgrade" kind on purpose. An unentitled pack is not shown at all
 * (ADR-0014 D9) — a till never teases a feature the business has not bought,
 * because the person reading it cannot buy it either.
 */
export type BlockerKind = "offline" | "blocked" | "missing";

const KIND_FILL: Record<BlockerKind, string> = {
  offline: "bg-fill-notice-weak",
  blocked: "bg-fill-error-weak",
  missing: "bg-fill-neutral-weak",
};

export function Blocker({
  kind = "blocked",
  title,
  children,
  action,
  actionLabel,
  className,
}: Readonly<{
  kind?: BlockerKind;
  title: React.ReactNode;
  /** What to do next. A stop with no next step is a dead end. */
  children: React.ReactNode;
  action?: () => void;
  actionLabel?: string;
  className?: string;
}>) {
  return (
    <div
      role={kind === "blocked" ? "alert" : "status"}
      className={cn(
        "flex min-h-[320px] flex-col items-center justify-center gap-3 rounded-tile border border-stroke-weak px-6 py-10 text-center",
        KIND_FILL[kind],
        className,
      )}
    >
      <ScreenTitle>{title}</ScreenTitle>
      <p className="max-w-[46ch] font-sans text-base text-text-weak">{children}</p>
      {action && actionLabel ? (
        <CounterButton tone="brand" size="default" onClick={action} className="mt-2">
          {actionLabel}
        </CounterButton>
      ) : null}
    </div>
  );
}
